import { z } from 'zod';
import {
  bdtAmountSchema,
  isoDateSchema,
  labelledUuid,
  transactionDirectionSchema,
  uuidSchema,
  type TransactionDirection,
} from './common.js';

const narrationBody = z
  .string({ message: 'Narration must be text' })
  .max(500, 'Narration must be at most 500 characters')
  .transform((s) => s.trim());

const voucherNoBody = z
  .string({ message: 'Voucher number is required' })
  .min(1, 'Voucher number is required')
  .max(50, 'Voucher number must be at most 50 characters')
  .transform((s) => s.trim());

export const createTransactionRequestSchema = z.object({
  fundId: labelledUuid('fund'),
  subHeadId: labelledUuid('sub-head').nullable().optional(),
  voucherNo: voucherNoBody,
  transactionDate: isoDateSchema,
  direction: transactionDirectionSchema,
  amount: bdtAmountSchema,
  narration: narrationBody.default(''),
});
export type CreateTransactionRequest = z.infer<typeof createTransactionRequestSchema>;

export const updateTransactionRequestSchema = z.object({
  subHeadId: labelledUuid('sub-head').nullable().optional(),
  voucherNo: voucherNoBody.optional(),
  transactionDate: isoDateSchema.optional(),
  direction: transactionDirectionSchema.optional(),
  amount: bdtAmountSchema.optional(),
  narration: narrationBody.optional(),
});
export type UpdateTransactionRequest = z.infer<typeof updateTransactionRequestSchema>;

export const transactionListQuerySchema = z.object({
  fundId: uuidSchema.optional(),
  subHeadId: uuidSchema.optional(),
  enteredByUserId: uuidSchema.optional(),
  direction: transactionDirectionSchema.optional(),
  /**
   * Free-text search across voucher number AND narration (case-insensitive
   * substring on both — see transaction.service.ts). Replaces the older
   * voucher-only `voucherSearch` param. 100 chars caps abuse without
   * truncating realistic narration searches.
   */
  q: z.string().max(100, 'Search term must be at most 100 characters').optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type TransactionListQuery = z.infer<typeof transactionListQuerySchema>;

/**
 * Frontend filter-form shape for the Transaction Ledger. Mirrors the audit
 * page pattern — all-string with empty defaults so each field round-trips
 * cleanly through URL search params via `useUrlFilters`. Caller is
 * responsible for clamping `direction` to a valid TransactionDirection
 * at use site (garbage values in the URL collapse to "All").
 */
export const transactionFilterFormSchema = z.object({
  dateFrom: z.string().default(''),
  dateTo: z.string().default(''),
  direction: z.string().default(''),
  subHeadId: z.string().default(''),
  q: z.string().default(''),
});
export type TransactionFilterForm = z.infer<typeof transactionFilterFormSchema>;

export interface TransactionResponse {
  id: string;
  fundId: string;
  fundCode: string;
  subHeadId: string | null;
  subHeadCode: string | null;
  subHeadName: string | null;
  voucherNo: string;
  transactionDate: string;
  direction: TransactionDirection;
  amount: string;
  narration: string;
  enteredByUserId: string;
  enteredByUsername: string;
  enteredByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface FundBalanceSummary {
  fundId: string;
  fundCode: string;
  fundName: string;
  openingBalance: string;
  totalCredits: string;
  totalDebits: string;
  currentBalance: string;
}

export interface MonthlyFlowPoint {
  /** YYYY-MM bucket label, Asia/Dhaka calendar. */
  month: string;
  credit: string;
  debit: string;
  net: string;
}

export interface SubHeadSpendPoint {
  subHeadId: string;
  subHeadCode: string;
  subHeadName: string;
  total: string;
}
